import { App, UserData } from 'hydra-data-types';
import { MedicareAccessScreenName, SharedScreenName } from '../../config/FlowTypes';
import { Environment } from '../../util/Environment';
import { MEDICARE_TYPE_SCREEN_KEYS } from '../../util/MedicareAccess';
import { syncInsuranceCoverageCheck } from './effects/SyncInsuranceCoverageCheck';
import { runActionSideEffect } from '../../util/StoreUtils';
import { ActionType } from '../ActionTypes';
import { store, StoreAction, StoreStateInternal } from '../Store';
import { grantUs65PlusAccess } from '../us65Plus/effects/GrantUs65PlusAccess';
import { updateUserDataInDb } from './effects/UpdateUserDataInDb';
import { UserDataState } from './UserDataState';

export type UserDataAction = {
  type: ActionType.MERGE_USER_DATA;
  userData: UserData;
};

const SESSION_KEYS_THAT_OVERWRITE_ACCOUNT_ON_LOGIN = [
  SharedScreenName.Age,
  SharedScreenName.DateOfBirth,
  SharedScreenName.Gender,
];

// The state is asked after the Medicare type, so a fresh type answer clears it with the other type
// keys. A stale state would otherwise decide eligibility (and the referred grant) before the re-ask.
const KEYS_CLEARED_BY_MEDICARE_TYPE_ANSWER = [
  ...MEDICARE_TYPE_SCREEN_KEYS,
  MedicareAccessScreenName.MedicareAccessState,
];

const SESSION_KEYS_EXCLUDED_FROM_LOGIN_MERGE = new Set([
  'initialSymptoms',
  'finishedSymptoms',
  'surveyUpdatedAt',
  'surveyUpdatedBy',
]);

function mergeUserDataOnLogin(
  accountUserData: UserDataState,
  sessionUserData: UserDataState
): UserDataState {
  const merged = { ...accountUserData } as Record<string, unknown>;
  const session = sessionUserData as Record<string, unknown>;

  // Preserve existing assessment answers, but retain answers for questions added since the
  // account's last assessment. Symptom summaries are historical program snapshots.
  for (const [key, value] of Object.entries(session)) {
    if (!(key in merged) && !SESSION_KEYS_EXCLUDED_FROM_LOGIN_MERGE.has(key)) {
      merged[key] = value;
    }
  }

  for (const key of SESSION_KEYS_THAT_OVERWRITE_ACCOUNT_ON_LOGIN) {
    if (key in session) merged[key] = session[key];
  }

  const answeredMedicareType = MEDICARE_TYPE_SCREEN_KEYS.some((key) => key in session);
  if (answeredMedicareType) {
    for (const key of KEYS_CLEARED_BY_MEDICARE_TYPE_ANSWER) {
      if (key in session) merged[key] = session[key];
      else delete merged[key];
    }
  }

  return merged as UserDataState;
}

export function userDataReducer(state: UserDataState, action: StoreAction): UserDataState {
  switch (action.type) {
    case ActionType.LOGIN_USER_SUCCESS: {
      if (Environment.APP !== App.Nerva) return action.userData;
      // Control users finish the coverage flow before signing up; file it now that they have.
      runActionSideEffect(syncInsuranceCoverageCheck);
      return mergeUserDataOnLogin(action.userData, state);
    }
    case ActionType.MERGE_USER_DATA: {
      runActionSideEffect(updateUserDataInDb); // Store in Firebase immediately if the user is logged in
      runActionSideEffect(grantUs65PlusAccess);
      runActionSideEffect(syncInsuranceCoverageCheck);
      const userData = { ...state, ...action.userData } as UserData;

      if (action.userData.initialSymptoms !== undefined) {
        userData.initialSymptoms = {
          ...state.initialSymptoms,
          ...action.userData.initialSymptoms,
        };
      }

      // The Medicare type can be answered on three different screens; a fresh answer must clear
      // the others or a stale account answer earlier in getMedicareType's order shadows it.
      const answeredTypeKey = MEDICARE_TYPE_SCREEN_KEYS.some((key) => key in action.userData);
      if (answeredTypeKey) {
        for (const key of KEYS_CLEARED_BY_MEDICARE_TYPE_ANSWER) {
          if (!(key in action.userData)) delete (userData as Record<string, unknown>)[key];
        }
      }
      return userData;
    }
    default:
      return state;
  }
}

export const UserDataSelector = {
  userData(state: StoreStateInternal): UserData {
    return state.userData;
  },
};

export function dispatchUserData(userData: UserDataState): void {
  store.dispatch({ type: ActionType.MERGE_USER_DATA, userData });
}

/** The `doctor` property is used as one of the indicators of if the user has been referred by a HCP. */
export function dispatchDoctorUserData({ referred }: { referred: 'Yes' | 'No' }): void {
  dispatchUserData({ [SharedScreenName.Doctor]: [referred] });
}
